import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import {
  spacing,
  breakpoints,
  palette,
  fontSize,
  fontStyle,
  Button,
  Typography,
} from '@playdapp/ui';
import { css } from '@emotion/react';
import { capitalize } from 'lodash';
import { useMedia } from 'react-use';
import { useInfiniteQuery } from 'react-query';

import { isTokenExist } from 'lib/auth';
import { getBreakpointQuery } from 'lib/util';
import { setIsMergeOpen } from 'store/merge';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getMyActivity, getMyMergeHistory } from 'api/user';
import useTokenCurrency from 'hooks/useTokenCurrency';

import type { EventType } from 'types/event';
import type {
  Activity,
  MergeHistory,
  UserActivityResponseData,
  UserMergeHistoryResponseData,
} from 'types/user';

import LoadingLayer, { LoadingBox } from '@/components/LoadingLayer';
import MyOffers from '@/components/MyOffers';
import Table from '@/components/Table';
import SortByDropDown from '@/components/SortByDropDown';
import MyItems from '@/components/MyItems2';
import MySales from '@/components/MySales2';
import MyBalance from '@/components/MyBalance2';
import MyFavorite from '@/components/MyFavorite2';
import EmptyItem from '@/components/EmptyItem';
import ErrorContents from '@/components/ErrorContents';
import MyProfile from '@/components/MyProfile';
import Spinner from '@/components/Spinner';

type MyPageProps = {
  tab: string;
};

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 4.5rem;
  overflow: unset;
  ${breakpoints.down('xl')} {
    padding-top: 4rem;
  }
  ${breakpoints.down('lg')} {
    display: block;
  }
  ${breakpoints.down('md')} {
    padding-top: 3.5rem;
  }
`;
const SideInfoContainer = styled.div`
  margin: ${spacing.m} 0 0 ${spacing.m};
  text-align: center;

  ${breakpoints.down('lg')} {
    padding: ${spacing.m} ${spacing.m} 0 ${spacing.m};
    margin: 0;
  }
  ${breakpoints.down('sm')} {
    padding: 0;
  }
`;
const SideInfoBlock = styled.div<{ isMdDown: boolean }>`
  padding: ${spacing.m};
  margin-bottom: ${spacing.m};
  background-color: white;
  border-radius: ${spacing.m};
  ${breakpoints.down('lg')} {
    display: flex;
    padding: ${spacing.xs};
    gap: ${spacing.m};
  }
  ${breakpoints.down('md')} {
    display: ${({ isMdDown }) => isMdDown && 'none'};
  }
  ${breakpoints.down('sm')} {
    gap: ${spacing.xxs};
    border-radius: 0;
  }
`;
const ContentSubHeadBlock = styled.div`
  width: 100%;
  padding: ${spacing.l};
  margin-top: ${spacing.xl};

  ${breakpoints.down('md')} {
    padding: 0 ${spacing.m} ${spacing.xxxl} ${spacing.m};
    margin-top: 0;
  }
`;
const MyPageMainTitle = styled.div`
  font-size: ${spacing.xl};
  font-weight: 600;
  margin-bottom: ${spacing.m};

  ${breakpoints.down('lg')} {
    display: none;
  }
`;
const ContentBody = styled.div<{ isLoading: boolean }>`
  position: relative;
  display: ${({ isLoading }) => (isLoading ? 'flex' : 'block')};
  align-items: center;
  justify-content: center;
  gap: ${spacing.l};
  margin: auto;
  min-height: 600px;

  ${breakpoints.down('xxl')} {
    gap: ${spacing.m};
  }
  ${breakpoints.down('xl')} {
    row-gap: ${spacing.l};
    column-gap: ${spacing.xl};
  }
  ${breakpoints.down('lg')} {
    padding-bottom: 2rem;
  }
  ${breakpoints.down('md')} {
    padding: 0;
  }
`;
const SubTitleTab = styled.div<{ isSelected: boolean; isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
  justify-content: center;
  padding: ${spacing.s} ${spacing.xxl};
  font-size: ${fontSize.b3};
  font-weight: ${({ isSelected }) => isSelected && 600};
  border-bottom: ${({ isSelected }) =>
    isSelected && `1px solid ${palette.dgray600}`};
  color: ${({ isSelected }) => (isSelected ? palette.black : palette.gray800)};
  cursor: pointer;
  &.activity-tab {
    text-transform: capitalize;
  }
  ${breakpoints.down('md')} {
    width: 100%;
    padding: ${spacing.s} 0;
    ${fontStyle('b4')};
    text-align: center;
  }
`;
const ItemTab = styled.button<{ isSelected: boolean; isBalance: boolean }>`
  display: ${({ isSelected }) => (isSelected ? 'flex' : 'block')};
  justify-content: flex-start;
  flex: 1;
  width: 100%;
  margin-bottom: ${({ isBalance }) => isBalance && 0};

  padding: ${spacing.s};
  ${fontStyle('b4')}
  border-radius: 10px;
  color: ${palette.black};
  background-color: ${({ isSelected }) =>
    isSelected ? palette.gray200 : palette.white};
  text-align: left;
  &.activity-tab {
    text-transform: capitalize;
  }
  &:hover {
    ${({ isSelected }) =>
      !isSelected &&
      css`
        color: ${palette.dgray600};
      `};
    background-color: ${({ isSelected }) =>
      isSelected ? palette.gray200 : palette.gray100};
  }
  &:active {
    background-color: ${palette.gray200};
  }
  ${breakpoints.down('lg')} {
    justify-content: center;
    text-align: center;
    border-radius: 10px;
  }
  ${breakpoints.down('md')} {
    min-width: ${({ theme }) => theme.rem(72)};
    max-width: ${({ theme }) => theme.rem(162)};
    padding: ${spacing.xs} ${spacing.l};
    font-size: ${fontSize.b5};
    font-weight: 700;
    border-radius: 6px;
    &:hover {
      ${({ isSelected }) =>
        !isSelected &&
        css`
          color: ${palette.dgray600};
        `};
      background-color: ${({ isSelected }) =>
        isSelected ? palette.dgray600 : palette.gray300};
    }
    &:active {
      color: ${palette.white};
      background-color: ${palette.dgray600};
    }
  }
`;
const TabNavContainer = styled.div`
  position: relative;
  ${breakpoints.down('lg')} {
    display: flex;
    width: 100%;
  }
`;
const SubTabNavContainer = styled.div<{ isVisible: string }>`
  display: flex;
  margin-bottom: ${({ isVisible }) => (isVisible ? spacing.l : 0)};
  border-bottom: ${({ isVisible }) =>
    isVisible !== 'balance' && `1px solid ${palette.gray400}`};
`;
const ItemOptionBox = styled.div`
  width: 100%;
  gap: 10px;
  & > div {
    width: 100%;
  }
`;
const MobileTabDrop = styled(SortByDropDown)`
  display: none;

  & > div {
    justify-content: flex-start;
    ${fontStyle('b1')};
    margin-bottom: ${spacing.m};
    gap: 0.5rem;
    border: none;
  }

  ${breakpoints.down('lg')} {
    display: block;
    order: 2;
  }

  ${breakpoints.down('md')} {
    width: 50%;
    margin: 0;

    & > div {
      margin-bottom: 6px;
    }
  }
`;
const DropDownSortContent = styled.div<{ isUnits: boolean }>`
  display: flex;
  flex-direction: column;
  max-height: ${({ isUnits }) => isUnits && '24rem'};
  overflow-y: ${({ isUnits }) => (isUnits ? 'scroll' : 'auto')};
  border-radius: ${spacing.xs};
  padding: 0;
  box-shadow: 0px 20px 32px 0px #4c4f6e1a;

  ${breakpoints.down('md')} {
    overflow-y: auto;
  }
`;

const DropDownItem = styled.button`
  display: flex;
  align-items: center;
  outline: 0;
  padding: ${spacing.s} ${spacing.l};
  background-color: white;
  text-align: left;
  font-family: initial;

  &:hover {
    background-color: ${palette.gray400};
  }

  & > span {
    flex: none;
    border-radius: 50%;
  }

  ${breakpoints.down('md')} {
    padding: ${spacing.xxs} ${spacing.m};

    & > p {
      width: 100%;
      padding: ${spacing.xs};
    }

    :hover {
      border-radius: ${spacing.xs};
    }
  }
`;
const tabItem = [
  { key: 'items', value: 'Items' },
  { key: 'activity', value: 'Activity' },
  // { key: 'balance', value: 'Balance' },
];
const subTabItem = [
  { key: 'my-items', value: 'My Items', type: 'items' },
  { key: 'my-sales', value: 'My Sales', type: 'items' },
  { key: 'favorite', value: 'Favorite', type: 'items' },
  { key: 'trading-history', value: 'Trading History', type: 'activity' },
  { key: 'offers', value: 'Offers', type: 'activity' },
];

const EmptyItemBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10rem;
  gap: ${spacing.xl};
  button {
    color: ${palette.white};
    font-weight: 600;
    border-radius: 1.5rem;
  }
`;

const LoadMore = styled(Button)`
  display: flex;
  align-items: center;
  height: ${spacing.xxl};
  padding: 6px ${spacing.m};
  margin: ${spacing.l} auto;
  border-radius: 6px;

  & + a,
  a + & {
    margin-left: ${spacing.xs};
  }
`;

const MyPageContent = ({ tab }: MyPageProps) => {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isClose, setIsClose] = useState(false);
  const [isMount, setIsMount] = useState(false);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [subTab, setSubTab] = useState('My Items');
  const [salesType, setSalesType] = useState<'bundle' | 'single'>('single');

  const router = useRouter();
  const dispatch = useAppDispatch();

  const ref = useRef<HTMLButtonElement>(null);

  const { isUserLoading, active } = useAppSelector(({ user }) => user);
  const { account } = useAppSelector(({ wallet }) => wallet);

  const isMdDownScreen = useMedia(getBreakpointQuery(breakpoints.down('md')));
  const pagePath = Object.keys(router.query)[0].toString();

  const CurrentPage = subTabItem.find((item) =>
    router.asPath.includes(item.key),
  );

  const { convertToUsd } = useTokenCurrency();

  const fetchActivityList = async ({ pageParam = 1 }) => {
    if (!account) {
      throw new Error('No account');
    }

    let tradingData: UserActivityResponseData[] = [];
    if (subTab === 'Trading History') {
      const { data } = await getMyActivity({
        account,
        type: capitalize('market'),
        page: pageParam,
      });

      if (data) tradingData = tradingData.concat(data);
    }

    let mergeData: UserMergeHistoryResponseData[] = [];
    if (subTab !== 'Trading History') {
      const { data } = await getMyMergeHistory({
        count: 20,
        offset: 0,
      });
      if (data) mergeData = mergeData.concat(data);
    }

    return {
      ...tradingData[0],
      ...mergeData[0],
      from: pageParam * 10,
      nextPage: pageParam + 1,
    };
  };

  const {
    isLoading,
    data: requestData,
    fetchNextPage,
    refetch,
    hasNextPage,
    error,
  } = useInfiniteQuery(['myActivity', account, subTab], fetchActivityList, {
    getNextPageParam: (table) => {
      if (
        Math.floor((table.total || table.count) / 10) >= table.nextPage &&
        (table.total || table.count) > 10
      ) {
        return table.nextPage;
      }
    },
    enabled: !!account,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

  const handleTabClick =
    (selectedTab: string) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      if (selectedTab === 'items') {
        dispatch(setIsMergeOpen(false));
      }
      if (selectedTab !== tab) {
        router.push(
          `/my-page/${selectedTab}?${
            (selectedTab === 'items' && 'my-items') ||
            (selectedTab === 'activity' && 'trading-history')
            //  || (selectedTab === 'balance' && 'balance')
          }`,
        );
      }
    };

  const handleSubTabClick = (subTab: string) => () => {
    setSubTab(subTab);
    if (subTab) {
      router.push(
        `/my-page/${tab}?${
          (subTab === 'My Items' && 'my-items') ||
          (subTab === 'My Sales' && 'my-sales') ||
          (subTab === 'Favorite' && 'favorite') ||
          (subTab === 'Trading History' && 'trading-history') ||
          (subTab === 'Offers' && 'offers')
        }`,
      );
    }
  };

  const handleCheckUrl = useMemo(
    () => () => {
      const checkUrl = subTabItem.find((menu) => pagePath.includes(menu.key));

      if (checkUrl?.key) {
        router.push(`/my-page/${tab}?${checkUrl?.key}`);
      }

      if (!checkUrl && tab) {
        router.push(
          `/my-page/${tab}?${
            (tab === 'items' && 'my-items') ||
            (tab === 'activity' && 'trading-history')
            //  || (tab === 'balance' && 'balance')
          }`,
        );
      }
    },
    [],
  );

  const handleLoadMore = () => {
    fetchNextPage();
  };

  useEffect(() => {
    if (account) {
      handleCheckUrl();
    }
  }, [account]);

  useEffect(() => {
    if (isClose) {
      setIsClose(() => false);
    }
  }, [isClose]);

  useEffect(() => {
    if (
      !isTokenExist() ||
      (!isUserLoading && (!account || !active) && isMount)
    ) {
      router.push('/');
    } else if (isPageLoading) {
      setIsPageLoading(false);
    }
  }, [
    account,
    active,
    router,
    isPageLoading,
    isUserLoading,
    isMount,
    handleCheckUrl,
  ]);

  useEffect(() => {
    if (isClose) {
      setIsClose(() => false);
    }
  }, [isClose]);

  useEffect(() => {
    if (tab === 'items') {
      setSubTab('My Items');
    }
    if (tab === 'activity') {
      setSubTab('Trading History');
    }
    if (tab === 'balance') {
      // setSubTab('');
      router.push('/my-page/items?my-items');
    }
  }, [tab]);

  useEffect(() => {
    if (isUserLoading) {
      setIsMount(true);
    }
  }, [isUserLoading]);

  useEffect(() => {
    if (
      account &&
      tab === 'balance' &&
      router.query.funds &&
      router.query.funds === 'buy'
    ) {
      setIsTransactionOpen(true);
    }
    if (
      account &&
      tab === 'sales' &&
      router.query.type &&
      (router.query.type === 'single' || router.query.type === 'bundle')
    ) {
      setSalesType(router.query.type);
    }
    return () => {
      setIsTransactionOpen(false);
      setSalesType('single');
    };
  }, [router.query, account]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (CurrentPage) {
      setSubTab(CurrentPage?.value);
    }
  }, [CurrentPage]);

  if (isPageLoading || isUserLoading) {
    return <LoadingLayer />;
  }

  return (
    <Container>
      <SideInfoContainer>
        <MyProfile />
        <SideInfoBlock isMdDown={isMdDownScreen}>
          <TabNavContainer>
            {tabItem.map((item) => (
              <ItemTab
                className="activity-tab"
                key={item.key}
                isSelected={tab === item.key}
                isBalance={item.key === 'balance'}
                onClick={handleTabClick(item.key)}
                ref={ref}
              >
                {item.value}
              </ItemTab>
            ))}
          </TabNavContainer>
        </SideInfoBlock>
      </SideInfoContainer>
      <ContentSubHeadBlock>
        {isMdDownScreen ? (
          <MobileTabDrop
            title={capitalize(tab)}
            width="100%"
            height="36px"
            top="40px"
            isClose={isClose}
            isBorder
            isDropBox
            floatingContent
            isMobile
          >
            <DropDownSortContent isUnits>
              {tabItem.map((info) => (
                <DropDownItem key={info.key} onClick={handleTabClick(info.key)}>
                  <Typography type="b3" color="dgray200" component="p">
                    {capitalize(info.key)}
                  </Typography>
                </DropDownItem>
              ))}
            </DropDownSortContent>
          </MobileTabDrop>
        ) : (
          <MyPageMainTitle>{capitalize(tab)}</MyPageMainTitle>
        )}
        <SubTabNavContainer isVisible={tab}>
          {subTabItem.map((item) => (
            <SubTitleTab
              className="activity-tab"
              key={`subtab-menu-${item.key}`}
              isVisible={item.type === tab}
              isSelected={pagePath === item.key}
              onClick={handleSubTabClick(item.value)}
            >
              {item.type === tab && item.value}
            </SubTitleTab>
          ))}
        </SubTabNavContainer>
        <ContentBody isLoading={isLoading}>
          {isLoading && (
            <LoadingBox>
              <Spinner />
            </LoadingBox>
          )}

          {!isLoading && (
            <ItemOptionBox>
              {subTab === 'My Items' && <MyItems />}
              {subTab === 'My Sales' && <MySales salesType={salesType} />}
              {subTab === 'Favorite' && <MyFavorite />}

              {subTab === 'Trading History' && (
                <>
                  <Table
                    title="TradingHistory"
                    headers={[
                      'Event',
                      'NFT Name',
                      'Price',
                      'From',
                      'To',
                      'Date',
                    ]}
                  >
                    {requestData?.pages.map((activityData) => {
                      const { activityList } = activityData;
                      return activityList?.map(
                        (info: Activity, idx: React.Key) => (
                          <Table.MyActivityItem
                            key={idx}
                            name={info.name}
                            event={info.event as EventType}
                            from={info.from}
                            to={info.to}
                            tokenId={info.tokenId}
                            isValid={info.isValid}
                            contractAddress={info.contractAddress}
                            price={info.price}
                            currency={info.currency}
                            timestamp={info.timestamp}
                            hash={info.hash}
                            slug={info.slug}
                            imageUri={info.imageUri}
                            network={info.network}
                            toUsd={convertToUsd(
                              info.price,
                              info.network,
                              info.currency,
                            )}
                            isOwner
                          />
                        ),
                      );
                    })}
                  </Table>
                  {hasNextPage && (
                    <LoadMore
                      onClick={handleLoadMore}
                      color="secondary"
                      size="md"
                      variant="outline"
                    >
                      <Typography color="dgray600" type="p5">
                        Load More +
                      </Typography>
                    </LoadMore>
                  )}

                  {!error &&
                    requestData?.pages &&
                    requestData?.pages.length > 0 &&
                    requestData?.pages[0].activityList.length === 0 && (
                      <EmptyItemBlock>
                        <EmptyItem text="No Trading yet" />
                      </EmptyItemBlock>
                    )}
                  {!isLoading && error && (
                    <EmptyItemBlock>
                      <ErrorContents />
                    </EmptyItemBlock>
                  )}
                </>
              )}

              {subTab === 'Merge history' && (
                <>
                  <Table
                    title="MergeHistory"
                    headers={[
                      'Merge Type',
                      'NFT Used',
                      'Result',
                      'Date',
                      'VRF',
                    ]}
                  >
                    {requestData?.pages.map((mergeData) => {
                      const { histories } = mergeData;
                      return histories?.map(
                        (info: MergeHistory, idx: React.Key) => (
                          <Table.MyMergeHistoryItem
                            key={idx}
                            date={info.date}
                            eventType={info.eventType}
                            materialToken={info.materialTokens}
                            resultTokens={info.resultTokens}
                            txhashVRF={info.txHashVRF}
                          />
                        ),
                      );
                    })}
                  </Table>

                  {hasNextPage && (
                    <LoadMore
                      onClick={handleLoadMore}
                      size="xs"
                      variant="outline"
                      color="secondary"
                    >
                      <Typography color="dgray600" type="p5">
                        Load More +
                      </Typography>
                    </LoadMore>
                  )}

                  {!error &&
                    requestData?.pages &&
                    requestData?.pages.length > 0 &&
                    requestData?.pages[0].histories.length === 0 && (
                      <EmptyItemBlock>
                        <EmptyItem text="No Merge yet" />
                      </EmptyItemBlock>
                    )}

                  {!isLoading && error && (
                    <EmptyItemBlock>
                      <ErrorContents />
                    </EmptyItemBlock>
                  )}
                </>
              )}
              {subTab === 'Offers' && <MyOffers />}
              {tab === 'balance' && (
                <MyBalance isTransactionOpen={isTransactionOpen} />
              )}
            </ItemOptionBox>
          )}
        </ContentBody>
      </ContentSubHeadBlock>
    </Container>
  );
};

export default MyPageContent;
